import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import type { PromoCode, ProductType } from '@Types/promoCode';
import { promoCodeTypeLabels, productTypeLabels } from '@Types/promoCode';
import { useTicketedEvents } from '@Hooks/useTicketedEvents';
import { useCreatePromoCode } from '@Hooks/useCreatePromoCode';
import { useUpdatePromoCode } from '@Hooks/useUpdatePromoCode';
import { MultiSelectCombobox } from '../MultiSelectCombobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../Select';
import { Switch } from '../Switch';

const FormSchema = z.object({
  code: z.string().min(1, { message: 'Code is required.' }),
  description: z
    .string()
    .optional()
    .transform((val) => (val && val.trim().length ? val.trim() : undefined)),
  codeType: z.enum(['SINGLE_USE', 'SINGLE_USE_GLOBAL', 'MULTI_USE']),
  discountType: z.enum(['PERCENTAGE', 'FLATAMOUNT']),
  discountValue: z.number().min(0, { message: 'Discount value is required.' }),
  maxRedemptions: z.number().optional().nullable(),
  validFrom: z.string().optional(),
  validUntil: z.string().optional().nullable(),
  isActive: z.boolean(),
  scope: z
    .array(z.enum(['TICKETED_EVENT']))
    .optional()
    .default([]),
  ticketedEventIds: z.array(z.string()).optional(),
});

type FormValues = z.input<typeof FormSchema>;

export interface PromoCodeSheetProps {
  promoCode?: PromoCode | null;
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
  onSuccess?: () => void;
}

const scopeItems = Object.entries(productTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

export default function PromoCodeSheet({
  promoCode,
  isOpen,
  setIsOpen,
  onSuccess,
}: PromoCodeSheetProps) {
  const isEditMode = !!promoCode;

  const createMutation = useCreatePromoCode();
  const updateMutation = useUpdatePromoCode();

  const inferredScope = useMemo((): ProductType[] => {
    if (promoCode?.scope && promoCode.scope.length > 0) return promoCode.scope;
    if (promoCode?.promoCodeTicketedEvents?.length) return ['TICKETED_EVENT'];
    return [];
  }, [promoCode]);

  const existingTicketedEventIds = useMemo(() => {
    if (!promoCode?.promoCodeTicketedEvents) return [];
    return promoCode.promoCodeTicketedEvents.map((pe) => pe.ticketedEventId);
  }, [promoCode]);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      code: promoCode?.code ?? '',
      description: promoCode?.description ?? '',
      codeType: promoCode?.codeType ?? 'SINGLE_USE',
      discountType:
        promoCode?.discountType === 'PERCENTAGE' ||
        promoCode?.discountType === 'FLATAMOUNT'
          ? promoCode.discountType
          : 'PERCENTAGE',
      discountValue: promoCode?.discountValue ?? 0,
      maxRedemptions: promoCode?.maxRedemptions ?? null,
      validFrom: promoCode?.validFrom
        ? new Date(promoCode.validFrom).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      validUntil: promoCode?.validUntil
        ? new Date(promoCode.validUntil).toISOString().split('T')[0]
        : null,
      isActive: promoCode?.isActive ?? true,
      scope: inferredScope,
      ticketedEventIds: existingTicketedEventIds,
    },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({
        code: promoCode?.code ?? '',
        description: promoCode?.description ?? '',
        codeType: promoCode?.codeType ?? 'SINGLE_USE',
        discountType:
          promoCode?.discountType === 'PERCENTAGE' ||
          promoCode?.discountType === 'FLATAMOUNT'
            ? promoCode.discountType
            : 'PERCENTAGE',
        discountValue: promoCode?.discountValue ?? 0,
        maxRedemptions: promoCode?.maxRedemptions ?? null,
        validFrom: promoCode?.validFrom
          ? new Date(promoCode.validFrom).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        validUntil: promoCode?.validUntil
          ? new Date(promoCode.validUntil).toISOString().split('T')[0]
          : null,
        isActive: promoCode?.isActive ?? true,
        scope: inferredScope,
        ticketedEventIds: existingTicketedEventIds,
      });
    }
  }, [promoCode, isOpen, form, inferredScope, existingTicketedEventIds]);

  const onSubmit = async (values: FormValues) => {
    form.clearErrors('root');

    const scope = values.scope ?? [];
    const payload = {
      code: values.code,
      description: values.description || undefined,
      codeType: values.codeType,
      discountType: values.discountType,
      discountValue: values.discountValue,
      maxRedemptions: values.maxRedemptions || undefined,
      validFrom: values.validFrom || undefined,
      validUntil: values.validUntil || undefined,
      isActive: values.isActive,
      scope,
      ticketedEventIds: scope.includes('TICKETED_EVENT')
        ? values.ticketedEventIds
        : [],
    };

    try {
      if (isEditMode && promoCode) {
        await updateMutation.mutateAsync({
          id: promoCode.id,
          payload,
        });
      } else {
        await createMutation.mutateAsync(payload);
      }

      onSuccess?.();
      setIsOpen(false);
    } catch (error) {
      form.setError('root', {
        type: 'server',
        message:
          error instanceof Error
            ? error.message
            : 'We were unable to save this Promo Code. Please check your changes and try again.',
      });
    }
  };

  const handleOnOpenChange = (newIsSheetOpen: boolean) => {
    setIsOpen(newIsSheetOpen);

    if (!newIsSheetOpen) {
      form.reset();
    }
  };

  const watchedCodeType = form.watch('codeType');
  const isSingleUse = useMemo(
    () =>
      watchedCodeType === 'SINGLE_USE' ||
      watchedCodeType === 'SINGLE_USE_GLOBAL',
    [watchedCodeType],
  );

  const watchedScope = form.watch('scope');
  const showTicketedEvents = useMemo(
    () => (watchedScope ?? []).includes('TICKETED_EVENT'),
    [watchedScope],
  );

  const ticketedEventsSearchParams = useMemo(() => {
    if (!isOpen || !showTicketedEvents) return {};
    return {
      'filter[startDateTime][gte]': new Date().toISOString(),
      'sort': 'name',
      'page[size]': '50',
    };
  }, [isOpen, showTicketedEvents]);

  const ticketedEvents = useTicketedEvents({
    searchParams: ticketedEventsSearchParams,
    enabled: isOpen && showTicketedEvents,
  });

  const ticketedEventItems = useMemo(() => {
    const raw = ticketedEvents.data;
    const events = Array.isArray(raw) ? raw : (raw?.data ?? []);
    const items = events.map((e: { id: string; name?: string }) => ({
      value: e.id,
      label: e.name ?? e.id,
    }));

    // Ensure already-associated events appear even if filtered out (e.g. past events)
    if (promoCode?.promoCodeTicketedEvents) {
      const existingIds = new Set(items.map((i) => i.value));
      promoCode.promoCodeTicketedEvents.forEach((pe) => {
        if (!existingIds.has(pe.ticketedEventId)) {
          items.push({
            value: pe.ticketedEventId,
            label: pe.ticketedEvent?.name ?? pe.ticketedEventId,
          });
        }
      });
    }

    return items;
  }, [ticketedEvents.data, promoCode]);

  const rootError = form.formState.errors.root?.message;
  const isSubmitting =
    form.formState.isSubmitting ||
    createMutation.isPending ||
    updateMutation.isPending;

  return (
    <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
      <SheetContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
            <div className="flex h-full flex-col">
              <SheetHeader>
                <SheetTitle>
                  {isEditMode ? `Edit ${promoCode.code}` : 'Create Promo Code'}
                </SheetTitle>
              </SheetHeader>

              <SheetBody className="grow overflow-auto">
                {rootError && (
                  <div className="mb-4">
                    <Alert variant="destructive">
                      <AlertTitle>Something went wrong</AlertTitle>
                      <AlertDescription>{rootError}</AlertDescription>
                    </Alert>
                  </div>
                )}

                <div className="space-y-6 py-3">
                  <FormField
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Code</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. SUMMER2024" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Optional description..."
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <FormField
                    control={form.control}
                    name="codeType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Code Type</FormLabel>
                        <Select
                          value={
                            field.value === 'SINGLE_USE_GLOBAL'
                              ? 'SINGLE_USE'
                              : field.value
                          }
                          onValueChange={(value) => {
                            field.onChange(value);
                            if (value === 'SINGLE_USE') {
                              form.setValue('maxRedemptions', null);
                            }
                          }}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select code type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="SINGLE_USE">
                              {promoCodeTypeLabels.SINGLE_USE}
                            </SelectItem>
                            <SelectItem value="MULTI_USE">
                              {promoCodeTypeLabels.MULTI_USE}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {isSingleUse && (
                    <FormField
                      control={form.control}
                      name="codeType"
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-1">
                          <div className="flex items-center gap-3">
                            <FormLabel>One total redemption</FormLabel>
                            <FormControl>
                              <Switch
                                checked={field.value === 'SINGLE_USE_GLOBAL'}
                                onCheckedChange={(checked) =>
                                  field.onChange(
                                    checked
                                      ? 'SINGLE_USE_GLOBAL'
                                      : 'SINGLE_USE',
                                  )
                                }
                              />
                            </FormControl>
                          </div>
                          <p className="text-muted-foreground text-sm">
                            {field.value === 'SINGLE_USE_GLOBAL'
                              ? 'This code can only be used once across all customers'
                              : 'Each customer can use this code once'}
                          </p>
                        </FormItem>
                      )}
                    />
                  )}

                  {!isSingleUse && (
                    <FormField
                      control={form.control}
                      name="maxRedemptions"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Max Redemptions</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={0}
                              placeholder="Leave empty for unlimited"
                              value={field.value ?? ''}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value === ''
                                    ? null
                                    : e.target.valueAsNumber,
                                )
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="discountType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Discount Type</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select discount type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="PERCENTAGE">
                              Percentage
                            </SelectItem>
                            <SelectItem value="FLATAMOUNT">
                              Flat Amount
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="discountValue"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Discount Value</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            placeholder="e.g. 10"
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(e.target.valueAsNumber || 0)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <FormField
                    control={form.control}
                    name="validFrom"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Valid From</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="validUntil"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Valid Until</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(e.target.value || null)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-3">
                        <FormLabel>Active</FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <FormField
                    control={form.control}
                    name="scope"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Applies To</FormLabel>
                        <MultiSelectCombobox
                          items={scopeItems}
                          selectedValues={field.value ?? []}
                          onChange={(values) => {
                            field.onChange(values);
                            if (!values.includes('TICKETED_EVENT')) {
                              form.setValue('ticketedEventIds', []);
                            }
                          }}
                          placeholder="Select product types..."
                          inModal
                        />
                        <p className="text-muted-foreground text-sm">
                          Leave empty for all product types.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {showTicketedEvents && (
                    <FormField
                      control={form.control}
                      name="ticketedEventIds"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ticketed Events</FormLabel>
                          <MultiSelectCombobox
                            items={ticketedEventItems}
                            selectedValues={field.value ?? []}
                            onChange={field.onChange}
                            placeholder="Select ticketed events..."
                            disabled={ticketedEvents.isLoading}
                            loading={ticketedEvents.isLoading}
                            inModal
                            footerHint={
                              ticketedEventItems.length >= 50
                                ? 'Refine results by searching...'
                                : undefined
                            }
                          />
                          <p className="text-muted-foreground text-sm">
                            Leave empty to apply to all ticketed events.
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </SheetBody>

              <SheetFooter>
                <Button type="submit" variant="primary" loading={isSubmitting}>
                  {isEditMode ? 'Update' : 'Create'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOnOpenChange(false)}
                >
                  Cancel
                </Button>
              </SheetFooter>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
